import { createContext, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { tableRows } from '@/canvas/engine';
import { key as cellKey } from '@/canvas/helpers';
import { DEF_BY_NO } from '@/canvas/defs/index';
import FieldControl, { normOptions } from '@/components/canvas/FieldControl';
import { canvasLabel, familyOf, stepSolid, stepText } from '@/components/canvas/bits';

// ------------------------------------------------------------------ context for one open canvas
const FormCtx = createContext(null);
export const FormProvider = FormCtx.Provider;
export const useForm = () => useContext(FormCtx);

const GRID4 = { 1: 'md:col-span-1', 2: 'md:col-span-2', 3: 'md:col-span-3', 4: 'md:col-span-4' };
const GRID12 = {
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

const choiceLike = (f) => f.kind === 'choice' || f.kind === 'multi' || f.kind === 'tag';

function widthOf(field) {
  if (field.w) return Number(field.w);
  if (field.kind === 'long') return 3;
  if (choiceLike(field)) return normOptions(field.options).length <= 2 ? 1 : 2;
  if (field.kind === 'number' || field.kind === 'calc') return 1;
  return 2;
}

function span4(field) {
  let w = Math.max(1, Math.min(4, field.w ? Number(field.w) : field.kind === 'long' ? 4 : widthOf(field)));
  if (field.kind === 'tag') w = Math.max(2, w);
  return GRID4[w];
}

function span12(field, total) {
  const min = field.kind === 'long' ? 4 : 3;
  const raw = Math.round((12 * widthOf(field)) / Math.max(1, total));
  return GRID12[Math.min(12, Math.max(min, raw))];
}

function requiredNow(field, ctx) {
  try {
    return typeof field.required === 'function' ? !!field.required(ctx) : !!field.required;
  } catch {
    return false;
  }
}

// ------------------------------------------------------------------ the inline guide
function GuideLine({ icon, title, tone, children }) {
  return (
    <div className="flex gap-2.5">
      <span
        className={cn(
          'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md',
          tone === 'warn' ? 'bg-warning-muted text-warning' : 'bg-primary/10 text-primary',
        )}
      >
        <ApperIcon name={icon} size={14} />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
        <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">{children}</p>
      </div>
    </div>
  );
}

export function GuidePanel({ guide, label, id }) {
  if (!guide) return null;
  return (
    <div id={id} role="note" className="col-span-full space-y-3 rounded-xl border border-primary/25 bg-accent/70 p-3.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">Guide: {label}</p>
      {guide.how && <GuideLine icon="PenLine" title="How to fill it in">{guide.how}</GuideLine>}
      {guide.example && <GuideLine icon="Lightbulb" title="NorthStar example">{guide.example}</GuideLine>}
      {guide.rule && <GuideLine icon="Ruler" title="The rule">{guide.rule}</GuideLine>}
      {guide.trap && <GuideLine icon="TriangleAlert" title="Watch out" tone="warn">{guide.trap}</GuideLine>}
    </div>
  );
}

function GuideButton({ open, onClick, label, controls }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-controls={controls}
      aria-label={`${open ? 'Hide' : 'Show'} the guide for ${label}`}
      className={cn(
        'inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-1.5 text-xs font-medium outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50',
        open ? 'bg-primary text-primary-foreground' : 'text-primary hover:bg-accent',
      )}
    >
      <ApperIcon name="CircleHelp" size={14} />
      <span className="hidden sm:inline">{open ? 'Hide guide' : 'Guide'}</span>
    </button>
  );
}

// ------------------------------------------------------------------ one field (label, control, guide)
function FieldCell({ field, fieldKey, spanClass, required }) {
  const f = useForm();
  const guideId = `guide-${fieldKey}`;
  const open = f.guides.isOpen(fieldKey);
  const invalid = f.showMissing && f.missing.has(fieldKey);
  const isCalc = field.kind === 'calc';
  return (
    <>
      <div className={cn('min-w-0 space-y-1.5', spanClass)} data-field={fieldKey}>
        <div className="flex items-start justify-between gap-2">
          <label htmlFor={`fld-${fieldKey}`} className="text-sm font-medium leading-snug">
            {field.label}
            {required && (
              <span className="text-destructive" aria-hidden="true">
                {' '}
                *
              </span>
            )}
            {isCalc && <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">auto</span>}
          </label>
          {field.guide && <GuideButton open={open} controls={guideId} label={field.label} onClick={() => f.guides.toggle(fieldKey)} />}
        </div>
        {field.help && <p className="text-xs leading-snug text-muted-foreground">{field.help}</p>}
        <FieldControl
          field={field}
          fieldKey={fieldKey}
          value={f.v[fieldKey]}
          calcValue={f.calc[fieldKey]}
          disabled={f.disabled}
          invalid={invalid}
          onValue={f.onValue}
        />
        {isCalc && field.rule && <p className="text-xs text-muted-foreground">Rule: {field.rule}</p>}
        {invalid && <p className="text-xs font-medium text-destructive">This one is still needed.</p>}
      </div>
      {open && field.guide && <GuidePanel id={guideId} guide={field.guide} label={field.label} />}
    </>
  );
}

// ------------------------------------------------------------------ blocks
function FieldsBlock({ block }) {
  const f = useForm();
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-4">
      {block.fields.map((field) => (
        <FieldCell
          key={field.id}
          field={field}
          fieldKey={field.id}
          spanClass={span4(field)}
          required={requiredNow(field, { v: f.v, up: f.up, d: f.state.d, row: null })}
        />
      ))}
    </div>
  );
}

function RowCard({ block, row, index }) {
  const f = useForm();
  const columns = block.columns;
  const total = columns.reduce((a, c) => a + widthOf(c), 0);
  const cells = {};
  columns.forEach((c) => {
    cells[c.id] = String(f.v[cellKey(block.id, row.id, c.id)] ?? '').trim();
  });
  return (
    <div className="rounded-xl border border-border bg-card p-3.5 shadow-xs md:p-4">
      <div className="mb-3 flex items-center gap-2.5">
        <span className={cn('flex size-7 shrink-0 items-center justify-center rounded-lg text-xs font-semibold', stepSolid(index), stepText(index))}>
          {row.n}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight">{row.label}</p>
          {row.sub && <p className="text-xs leading-snug text-muted-foreground">{row.sub}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-12">
        {columns.map((col) => {
          const k = cellKey(block.id, row.id, col.id);
          return (
            <FieldCell
              key={k}
              field={col}
              fieldKey={k}
              spanClass={span12(col, total)}
              required={requiredNow(col, { v: f.v, up: f.up, d: f.state.d, row: cells, rowInfo: row })}
            />
          );
        })}
      </div>
    </div>
  );
}

function TableBlock({ block }) {
  const f = useForm();
  const rows = useMemo(() => tableRows(block, f.v, f.up), [block, f.v, f.up]);
  const managed = !(block.rows && (block.rows.fixed || block.rows.from));
  const max = block.rows?.max ?? rows.length;
  const start = block.rows?.start ?? block.rows?.min ?? 1;
  const noun = String(block.rows?.label || 'row').toLowerCase();
  const last = rows[rows.length - 1];
  const lastEmpty = last ? block.columns.every((c) => c.kind === 'calc' || !String(f.v[cellKey(block.id, last.id, c.id)] ?? '').trim()) : false;
  const countKey = `${block.id}._n`;
  return (
    <section className="space-y-3">
      {block.title && <h4 className="font-heading text-sm font-semibold text-foreground">{block.title}</h4>}
      {rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          This list fills itself from an earlier canvas. Confirm that canvas and the rows appear here.
        </div>
      )}
      {rows.map((row, i) => (
        <RowCard key={row.id} block={block} row={row} index={i} />
      ))}
      {managed && !f.disabled && (
        <div className="flex flex-wrap items-center gap-2">
          {rows.length < max && (
            <Button type="button" variant="outline" size="sm" onClick={() => f.onValue(countKey, String(rows.length + 1))}>
              <ApperIcon name="Plus" size={16} />
              Add {noun}
            </Button>
          )}
          {rows.length > start && lastEmpty && (
            <Button type="button" variant="ghost" size="sm" onClick={() => f.onValue(countKey, String(rows.length - 1))}>
              <ApperIcon name="Minus" size={16} />
              Remove empty {noun}
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            {rows.length} of up to {max}
          </span>
        </div>
      )}
    </section>
  );
}

function PullBlock({ block }) {
  const f = useForm();
  const fam = familyOf(f.def);
  return (
    <section className={cn('rounded-xl border border-l-4 border-border bg-card p-4', fam.edgeLeft)}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ApperIcon name="ArrowDownToLine" size={16} className={fam.text} />
        <h4 className="font-heading text-sm font-semibold">{block.title}</h4>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">pulled in, read only</span>
      </div>
      <dl className="grid gap-4 md:grid-cols-2">
        {block.items.map((it) => {
          const raw = f.state.pulled?.[`${block.id}.${it.id}`];
          const has = raw !== null && raw !== undefined && String(raw).trim() !== '';
          const src = it.no !== undefined && it.no !== null ? DEF_BY_NO[it.no] : null;
          return (
            <div key={it.id} className="min-w-0 space-y-1">
              <dt className="flex flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {it.label}
                {it.ref && <span className="rounded bg-accent px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-accent-foreground">{it.ref}</span>}
              </dt>
              <dd className={cn('whitespace-pre-line text-sm leading-relaxed', !has && 'italic text-muted-foreground')}>
                {has ? String(raw) : src ? `Not filled in yet on ${canvasLabel(src)}.` : 'Not filled in yet.'}
              </dd>
              {it.note && has && <p className="text-xs text-muted-foreground">{it.note}</p>}
              {src && (
                <Link to={`/canvas/${src.no}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                  {has ? `Change on ${canvasLabel(src)}` : `Open ${canvasLabel(src)}`}
                  <ApperIcon name="ArrowRight" size={12} />
                </Link>
              )}
            </div>
          );
        })}
      </dl>
    </section>
  );
}

const NOTE_TONE = {
  info: { icon: 'Info', cls: 'border-primary/25 bg-accent/70' },
  rule: { icon: 'Ruler', cls: 'border-warning-border bg-warning-muted' },
  warn: { icon: 'TriangleAlert', cls: 'border-destructive/40 bg-destructive/10' },
};

function NoteBlock({ block }) {
  const tone = NOTE_TONE[block.tone] || NOTE_TONE.info;
  return (
    <div className={cn('flex gap-2.5 rounded-xl border p-3.5 text-sm leading-relaxed', tone.cls)}>
      <ApperIcon name={tone.icon} size={16} className="mt-0.5 shrink-0" />
      <p className="whitespace-pre-line">{block.text}</p>
    </div>
  );
}

function ReferenceBlock({ block }) {
  return (
    <Collapsible className="rounded-xl border border-border bg-card">
      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
        <span className="flex items-center gap-2">
          <ApperIcon name="BookOpen" size={16} className="text-primary" />
          {block.title}
        </span>
        <ApperIcon name="ChevronDown" size={16} className="shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="overflow-x-auto border-t border-border px-4 pb-4 pt-3">
          <table className="w-full min-w-[34rem] border-collapse text-left text-xs leading-relaxed">
            <thead>
              <tr>
                {(block.head || []).map((h) => (
                  <th key={h} scope="col" className="border-b border-border bg-muted/60 px-2.5 py-2 font-semibold text-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(block.rows || []).map((r, i) => (
                <tr key={`${r[0]}-${i}`} className="align-top">
                  {r.map((cell, j) => (
                    <td key={`${i}-${j}`} className={cn('border-b border-border px-2.5 py-2', j === 0 && 'font-semibold')}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function StepBlocks({ blocks }) {
  return (
    <div className="space-y-5">
      {blocks.map((block, i) => {
        const k = `${block.type}-${block.id || i}`;
        if (block.type === 'fields') return <FieldsBlock key={k} block={block} />;
        if (block.type === 'table') return <TableBlock key={k} block={block} />;
        if (block.type === 'pull') return <PullBlock key={k} block={block} />;
        if (block.type === 'note') return <NoteBlock key={k} block={block} />;
        if (block.type === 'reference') return <ReferenceBlock key={k} block={block} />;
        return null;
      })}
    </div>
  );
}

// a field made by hand (the sign-off) uses the same cell as a field from a definition
export { FieldCell };
